import {useEffect, useRef} from "react";
import axios from "axios";
import {useDispatch, useSelector} from "react-redux";
import {useRouter} from "next/router";
import {useTranslation} from "next-i18next";
import {toast} from "react-toastify";
import {Customer, ShopState} from "../../interfaces/store-interfaces";
import {isJwtValid} from "../../services/customer-services";
import {logout} from "../../store/action";

const SESSION_EXPIRED_TOAST_ID = 'session-expired';

const SessionGuard = () => {

    const {t} = useTranslation('common');
    const router = useRouter();
    const dispatch = useDispatch();
    const customer: Customer = useSelector((state: ShopState) => state.customer);

    // refs so the interceptor (registered once) always sees fresh values
    const routerRef = useRef(router);
    const tRef = useRef(t);
    routerRef.current = router;
    tRef.current = t;

    // JWT expiring while browsing (expiry at load is handled in _app before the first render)
    useEffect(() => {
        if (customer !== undefined && customer !== null && !isJwtValid(customer.jwt)) {
            dispatch(logout());
        }
    }, [customer, router.asPath]);

    // JWT rejected by the API on an authenticated request: logout and ask to login again
    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            response => response,
            error => {
                const headers = error?.config?.headers;
                const hasJwt = headers !== undefined && (headers.Authorization !== undefined || headers.authorization !== undefined);
                if (error?.response?.status === 401 && hasJwt) {
                    dispatch(logout());
                    toast(tRef.current('TOAST_SESSION_EXPIRED'), {
                        toastId: SESSION_EXPIRED_TOAST_ID,
                        autoClose: 4000,
                        type: toast.TYPE.ERROR,
                        position: toast.POSITION.TOP_CENTER
                    });
                    const currentRouter = routerRef.current;
                    if (!currentRouter.pathname.startsWith('/login')) {
                        currentRouter.push('/login?redirect=' + encodeURIComponent(currentRouter.asPath));
                    }
                }
                return Promise.reject(error);
            }
        );
        return () => axios.interceptors.response.eject(interceptor);
    }, []);

    return null;
}

export default SessionGuard;
